import { MetricStat } from '@/shared/components/IconButton'
import { StarIcon, CommitIcon, TagIcon, DownloadIcon } from '@/shared/components/Icons'
import type { ProviderArtefactRow, ProviderVcsStats } from '@/lib/apiTypes'
import { formatCount } from '@/lib/format'

/** Compact repo-health + downloads run for a list row: stars / commits 90d / releases 90d /
 *  downloads 90d, closing with the source-repo link. VCS numbers come from the cached
 *  provider_vcs_stats row (null until fetched - rendered as a single muted dash with the reason). */
export function MetricsCell({ row }: { row: ProviderArtefactRow }) {
  const vcs = row.vcs_stats
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
      {vcs ? (
        <>
          <MetricStat icon={<StarIcon />} value={vcs.stars != null ? formatCount(vcs.stars) : '—'} tip={starsTip(vcs)} />
          <MetricStat
            icon={<CommitIcon />}
            value={vcs.recent_commits != null ? `${formatCount(vcs.recent_commits)}${vcs.recent_commits_capped ? '+' : ''}` : '—'}
            tip="Commits on the source repository in the last 90 days"
          />
          <MetricStat icon={<TagIcon />} value={vcs.releases_90d != null ? formatCount(vcs.releases_90d) : '—'} tip="Releases published in the last 90 days" />
        </>
      ) : (
        <MetricStat
          icon={<StarIcon />}
          value="—"
          tip={row.source_url
            ? `No repository stats yet${vcsErrorSuffix(row)}`
            : 'No resolvable source repository'}
        />
      )}
      <MetricStat icon={<DownloadIcon />} value={formatCount(row.downloads.last_90d)} tip="Downloads through Terramantle in the last 90 days" />
    </span>
  )
}

/** Stars tooltip: names the parent when the source repo is a fork (stats are taken from the
 *  repo development happens on, e.g. hashicorp/terraform-provider-http behind OpenTofu's fork). */
export function starsTip(vcs: ProviderVcsStats): string {
  return vcs.is_fork && vcs.parent_repo
    ? `Stars on ${vcs.parent_repo} (this provider is served from a fork of it)`
    : 'Stars on the source repository'
}

export function vcsErrorSuffix(row: ProviderArtefactRow): string {
  return row.vcs_stats?.fetch_error ? ` (${row.vcs_stats.fetch_error})` : ''
}

// ── Artefact detail modal (M3 seed: versions table) ───────────────────────────

/** CVSS score → severity-band colour (≥9 critical, ≥7 high, ≥4 medium), matching SEVERITY_COLOR. */
export function cvssColor(score: number): string {
  return score >= 9 ? 'var(--red)' : score >= 7 ? 'var(--amber)' : score >= 4 ? 'var(--yellow)' : 'var(--text)'
}
